/**
 * PATH: packages/email-hub/src/feature/components/email-preview-pane/usePreviewToolbarActions.tsx
 * FILE_ID: EMAILHUB-EMAILPREVIEWPANE-TOOLBAR-HOOK-001
 * PURPOSE: Build and rank preview actions while preserving mobile grouping.
 */
import { useCallback, useMemo, type MutableRefObject } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Archive,
  CheckCircle2,
  Forward,
  Inbox,
  Link2,
  Loader2,
  Reply,
  ReplyAll,
  Star,
  Trash2,
} from 'lucide-react'
import { captureQuoteSelection } from '../../lib/quoteSelection'
import { emailPath } from '../../lib/basePath'
import { composePreviewLocationState } from '../../pages/composePreviewState'
import { useSendEmailToInbox } from '../../hooks/useSendEmailToInbox'
import type { Email, useEmailActions } from '../../hooks/useEmails'
import type { useToast } from '../../../_internal/components/Toast'
import type { PreviewToolbarAction } from './types'

/**
 * Mobile (<md) pinned cluster contract — BUG-2026-06-23-EMAIL-MOBILE-REPLY-CROWDED.
 * Explicit id allowlist, NOT a positional slice: renaming/reordering/inserting
 * actions above must never silently change which 3 buttons stay visible on a
 * narrow phone. Pinned set is user-visible Reply / Reply All / Forward only;
 * everything else (including newer `done`/`star` actions) lives in the
 * overflow menu. See EmailPreviewPane.mobileOverflow.test.ts for the pin.
 */
export const MOBILE_PINNED_ACTION_IDS = ['reply', 'reply_all', 'forward']

interface PreviewToolbarOptions {
  email: Email
  kanbanOrgId: number | null
  archive: ReturnType<typeof useEmailActions>['archive']
  bulkAction: ReturnType<typeof useEmailActions>['bulkAction']
  toggleStar: ReturnType<typeof useEmailActions>['toggleStar']
  toast: ReturnType<typeof useToast>['toast']
  findBodyRef: MutableRefObject<HTMLDivElement | null>
  onRefetch?: () => void
  onResolved?: (emailId: string) => void
  setShowLinkModal: (open: boolean) => void
}

export function usePreviewToolbarActions({
  email,
  kanbanOrgId,
  archive,
  bulkAction,
  toggleStar,
  toast,
  findBodyRef,
  onRefetch,
  onResolved,
  setShowLinkModal,
}: PreviewToolbarOptions) {
  const navigate = useNavigate()
  const { mutate: sendToInbox, pending: sendingToInbox } =
    useSendEmailToInbox()

  const sendEmailToInbox = useCallback(async () => {
    if (!kanbanOrgId) {
      toast('Kanban not available - refresh and try again.', 'error')
      return
    }
    try {
      const result = await sendToInbox({
        emailId: String(email.id),
        organisation: kanbanOrgId,
      })
      toast(
        result.created ? 'Sent to your Inbox' : 'Already in your Inbox',
        'success',
      )
    } catch (error) {
      const message =
        (error as Error).message ??
        ((error as { status?: number }).status === 404
          ? 'Set up your Kanban workspace first.'
          : 'Could not send to Inbox.')
      toast(message, 'error')
    }
  }, [email.id, kanbanOrgId, sendToInbox, toast])

  const previewToolbarActions = useMemo<PreviewToolbarAction[]>(
    () => [
      {
        id: 'reply',
        title: 'Reply',
        onClick: (track) => {
          track()
          captureQuoteSelection(String(email.id), findBodyRef.current)
          navigate(`${emailPath('/compose')}?reply=${email.id}`, {
            state: composePreviewLocationState(String(email.id)),
          })
        },
        icon: <Reply className="h-3.5 w-3.5 text-muted-foreground" />,
      },
      {
        id: 'reply_all',
        title: 'Reply All',
        onClick: (track) => {
          track()
          captureQuoteSelection(String(email.id), findBodyRef.current)
          navigate(`${emailPath('/compose')}?reply_all=${email.id}`, {
            state: composePreviewLocationState(String(email.id)),
          })
        },
        icon: <ReplyAll className="h-3.5 w-3.5 text-muted-foreground" />,
      },
      {
        id: 'forward',
        title: 'Forward',
        onClick: (track) => {
          track()
          navigate(`${emailPath('/compose')}?forward=${email.id}`, {
            state: composePreviewLocationState(String(email.id)),
          })
        },
        icon: <Forward className="h-3.5 w-3.5 text-muted-foreground" />,
      },
      {
        id: 'done',
        title: 'Done',
        onClick: (track) => {
          track()
          onResolved?.(email.id)
          void archive(email.id, {
            folder: email.folder,
            isRead: email.is_read,
            isStarred: email.is_starred,
            complete: true,
          }).catch(() => onRefetch?.())
        },
        icon: <CheckCircle2 className="h-3.5 w-3.5 text-muted-foreground" />,
      },
      {
        id: 'link_job',
        title: 'Link to Job',
        onClick: (track) => {
          track()
          setShowLinkModal(true)
        },
        icon: <Link2 className="h-3.5 w-3.5 text-muted-foreground" />,
      },
      {
        id: 'send_to_inbox',
        title: 'Send to Kanban Inbox',
        onClick: (track) => {
          track()
          void sendEmailToInbox()
        },
        icon: sendingToInbox ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
        ) : (
          <Inbox className="h-3.5 w-3.5 text-muted-foreground" />
        ),
        disabled: sendingToInbox,
        testId: 'email-send-to-inbox',
      },
      {
        id: 'star',
        title: email.is_starred ? 'Unstar' : 'Star',
        onClick: (track) => {
          track()
          void toggleStar(email.id, email.is_starred)
            .then(() => onRefetch?.())
            .catch(() => onRefetch?.())
        },
        icon: (
          <Star
            className={`h-3.5 w-3.5 ${
              email.is_starred
                ? 'fill-amber-400 text-amber-500'
                : 'text-muted-foreground'
            }`}
          />
        ),
      },
      {
        id: 'archive',
        title: 'Archive',
        onClick: (track) => {
          track()
          onResolved?.(email.id)
          void archive(email.id)
            .then(() => onRefetch?.())
            .catch(() => onRefetch?.())
        },
        icon: <Archive className="h-3.5 w-3.5 text-muted-foreground" />,
      },
      {
        id: 'delete',
        title: 'Delete',
        onClick: (track) => {
          track()
          onResolved?.(email.id)
          void bulkAction([email.id], 'delete')
            .then(() => onRefetch?.())
            .catch(() => onRefetch?.())
        },
        icon: <Trash2 className="h-3.5 w-3.5" />,
        destructive: true,
      },
    ],
    [
      archive,
      bulkAction,
      email.id,
      findBodyRef,
      navigate,
      onRefetch,
      onResolved,
      sendEmailToInbox,
      sendingToInbox,
      setShowLinkModal,
      toggleStar,
    ],
  )

  return {
    previewActions: previewToolbarActions,
  }
}
